import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  Clock3,
  ExternalLink,
  FileCheck2,
  FileSearch,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import {
  projectDocumentStatusService,
  type ProjectDocumentCheck,
  type ProjectDocumentCheckStatus,
  type ProjectDocumentStatusResponse,
} from '@/services/projectDocumentStatusService';
import { Button, Drawer, Modal } from '@/ui/components';
import './ProjectDocumentStatusPanel.css';

type ProjectDocumentStatusPanelProps = {
  open: boolean;
  onClose: () => void;
  projectId: number;
  projectNumber: string;
  presentation?: 'drawer' | 'modal';
};

const STATUS_PRESENTATION: Record<ProjectDocumentCheckStatus, { label: string; icon: React.ReactNode }> = {
  available: { label: 'Available', icon: <CheckCircle2 size={18} aria-hidden="true" /> },
  incomplete: { label: 'Incomplete', icon: <AlertTriangle size={18} aria-hidden="true" /> },
  missing: { label: 'Missing', icon: <XCircle size={18} aria-hidden="true" /> },
  ambiguous: { label: 'Review', icon: <CircleHelp size={18} aria-hidden="true" /> },
  not_checked: { label: 'Not checked', icon: <Clock3 size={18} aria-hidden="true" /> },
};

const DISCIPLINE_LABELS: Record<string, string> = {
  architectural: 'Architectural inputs',
  project_inputs: 'Site information',
  civil: 'Civil design',
  structural: 'Structural design',
};

function groupChecks(checks: ProjectDocumentCheck[]) {
  return checks.reduce<Record<string, ProjectDocumentCheck[]>>((groups, check) => {
    (groups[check.discipline] ||= []).push(check);
    return groups;
  }, {});
}

function StatusMark({ status }: { status: ProjectDocumentCheckStatus }) {
  const presentation = STATUS_PRESENTATION[status];
  return <span className={`project-status-mark project-status-mark--${status}`}>{presentation.icon}<span>{presentation.label}</span></span>;
}

function ProjectStatusCheck({ check }: { check: ProjectDocumentCheck }) {
  return (
    <article className={`project-status-check project-status-check--${check.status}`}>
      <div className="project-status-check__rail" aria-hidden="true" />
      <div className="project-status-check__body">
        <div className="project-status-check__heading">
          <div><h4>{check.label}</h4><p>{check.summary}</p></div>
          <StatusMark status={check.status} />
        </div>
        {check.evidence.length > 0 ? (
          <details className="project-status-evidence">
            <summary>{check.evidence.length} SharePoint evidence file{check.evidence.length === 1 ? '' : 's'}</summary>
            <div className="project-status-evidence__list">
              {check.evidence.map((evidence) => (
                <div className="project-status-evidence__item" key={`${check.id}-${evidence.itemId || evidence.relativePath}`}>
                  <FileCheck2 size={16} aria-hidden="true" />
                  <div>
                    {evidence.webUrl ? (
                      <a href={evidence.webUrl} target="_blank" rel="noreferrer"><strong>{evidence.fileName}</strong><ExternalLink size={13} aria-hidden="true" /></a>
                    ) : <strong>{evidence.fileName}</strong>}
                    <span>{evidence.relativePath}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        ) : null}
      </div>
    </article>
  );
}

export const ProjectDocumentStatusPanel: React.FC<ProjectDocumentStatusPanelProps> = ({
  open,
  onClose,
  projectId,
  projectNumber,
  presentation = 'drawer',
}) => {
  const [response, setResponse] = useState<ProjectDocumentStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      setResponse(await projectDocumentStatusService.getLatest(projectId));
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load project document status.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) void loadStatus();
  }, [open, loadStatus]);

  const refreshStatus = async () => {
    setRefreshing(true);
    setError(null);
    try {
      setResponse(await projectDocumentStatusService.refresh(projectId));
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Unable to refresh SharePoint document status.');
    } finally {
      setRefreshing(false);
    }
  };

  const result = response?.result;
  const coverageIncomplete = result?.scan.status === 'incomplete' || result?.scan.truncated === true;
  const groupedChecks = useMemo(() => groupChecks((result?.checks || []).map(check => coverageIncomplete
    ? { ...check, status: 'not_checked' as const, summary: 'Coverage is incomplete. Observed evidence is retained; missing documents and latest versions are not determined.' }
    : check)), [result?.checks, coverageIncomplete]);
  const availableCount = result?.summary.available || 0;
  const totalChecks = result?.checks.length || 9;
  const hasAttention = Boolean(result && (result.summary.missing > 0 || result.summary.incomplete > 0 || result.summary.ambiguous > 0));

  const footer = (
    <div className="project-status-footer">
      <span>SharePoint source · cached up to 24 hours</span>
      <Button variant="primary" iconBefore={<RefreshCw size={16} aria-hidden="true" />} onClick={() => void refreshStatus()} loading={refreshing}>
        Refresh from SharePoint
      </Button>
    </div>
  );

  const content = (
    <div className="project-status-panel" aria-live="polite">
        <section className="project-status-hero">
          <div className="project-status-hero__eyebrow"><FileSearch size={16} aria-hidden="true" />Project {projectNumber || result?.project.projectNumber}</div>
          <div className="project-status-hero__main">
            <div>
              <h3>{coverageIncomplete ? 'Coverage incomplete' : result ? `${availableCount} of ${totalChecks} checks available` : 'Document evidence check'}</h3>
              <p>{coverageIncomplete ? 'Missing documents and the latest versions cannot be determined from this partial scan.' : result ? (hasAttention ? 'Some SharePoint documents need attention or human review.' : 'All configured SharePoint document checks have evidence.') : 'Checking the latest project document status.'}</p>
            </div>
            {result && !coverageIncomplete ? <div className={`project-status-score ${hasAttention ? 'project-status-score--attention' : ''}`}><strong>{availableCount}</strong><span>/ {totalChecks}</span></div> : null}
          </div>
          {response?.cache.scannedAt ? (
            <div className="project-status-hero__meta">
              Scanned {response.cache.scannedAt.value} · {response.cache.state === 'fresh' ? 'SQL cache' : response.cache.state === 'refreshed' ? 'Refreshed from SharePoint' : 'Stale cache'}
              {response.cache.sharePointFolderUrl ? <a href={response.cache.sharePointFolderUrl} target="_blank" rel="noreferrer">Open folder <ExternalLink size={13} aria-hidden="true" /></a> : null}
            </div>
          ) : null}
        </section>

        {loading ? <div className="project-status-state"><RefreshCw className="project-status-spin" size={22} aria-hidden="true" /><div><strong>Checking document status</strong><span>Using the SQL cache or refreshing from SharePoint when older than 24 hours…</span></div></div> : null}
        {error ? <div className="project-status-state project-status-state--error" role="alert"><XCircle size={22} aria-hidden="true" /><div><strong>Status unavailable</strong><span>{error}</span></div></div> : null}
        {!loading && response?.cache.state === 'stale' ? <div className="project-status-state project-status-state--waiting" role="status"><AlertTriangle size={22} aria-hidden="true" /><div><strong>Showing stale cached evidence</strong><span>SharePoint refresh failed: {response.refreshError || 'unknown error'}</span></div></div> : null}

        {result ? (
          <div className="project-status-groups">
            {Object.entries(groupedChecks).map(([discipline, checks]) => (
              <section className="project-status-group" key={discipline}>
                <div className="project-status-group__title"><span>{DISCIPLINE_LABELS[discipline] || discipline.replace(/_/g, ' ')}</span><small>{checks.length} check{checks.length === 1 ? '' : 's'}</small></div>
                <div className="project-status-group__checks">{checks.map((check) => <ProjectStatusCheck check={check} key={check.id} />)}</div>
              </section>
            ))}
            <div className="project-status-delivery-note"><AlertTriangle size={18} aria-hidden="true" /><div><strong>Delivery is not verified</strong><span>{result.deliveryVerification.summary}</span></div></div>
            {result.warnings.length > 0 ? <div className="project-status-warnings">{result.warnings.map((warning) => <p key={warning}>{warning}</p>)}</div> : null}
          </div>
        ) : null}
    </div>
  );

  if (presentation === 'modal') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={`Project document status · ${projectNumber}`}
        size="xl"
        className="project-status-modal"
        footer={footer}
      >
        {content}
      </Modal>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Project document status"
      width="min(680px, 100vw)"
      className="project-status-drawer"
      footer={footer}
    >
      {content}
    </Drawer>
  );
};
